export const CUSTOMER_PORTAL_ROUTES = {
  LOGIN: '/customer-portal/login',
  /**
   * B6.14 — invitation activation and password recovery. Deliberately a sibling of
   * LOGIN, outside the CustomerProtectedRoute boundary: an invited customer has no
   * session yet, and requiring one would make the page unreachable by the only people
   * who need it.
   */
  ACTIVATE: '/customer-portal/activate',
  DASHBOARD: '/customer-portal/dashboard',
  EQUIPMENT: '/customer-portal/equipment',
  EQUIPMENT_DETAIL: '/customer-portal/equipment/:equipmentId',
  JOBS: '/customer-portal/jobs',
  JOB_DETAIL: '/customer-portal/jobs/:jobId',
  /**
   * Stage D-i (D-145 / OD-D1) — the canonical customer Request Thread. Keyed by
   * the ROUND, because one customer request is one modification round and that
   * is the only id a notification deep link carries; the job-keyed routes scope
   * by both ids and cannot be reached with a round id alone.
   *
   * Not to be confused with the STAFF `ROUTES.REQUEST_THREAD` (`/requests/:roundId`).
   * The two live in different application branches with different route tables,
   * different API clients and different tokens (D-050), so neither can produce
   * or reach the other's URL. There is deliberately no `/customer-portal/requests`
   * list route: the thread is reached from a job, a notification or a link, and
   * a browse surface is not part of D-i.
   */
  REQUEST_THREAD: '/customer-portal/requests/:roundId',
  /** D-130 Phase 6C — the full decision queue. The dashboard links here rather
   * than rendering every pending decision as a card. */
  DECISIONS: '/customer-portal/decisions',
  /** D-130 Phase 6B — theme (six skins) and light/dark mode. Presentation only:
   * no permission of its own beyond an authenticated session, and no effect on
   * data, scope or available actions. */
  APPEARANCE: '/customer-portal/appearance',
  /** User Profile P2 — the signed-in contact's own account (details, sign-in,
   * organisation, location(s)). Self-scoped: the page never carries an id. */
  ACCOUNT: '/customer-portal/account',
  /** User Profile P2 — change own password; also where CustomerProtectedRoute
   * sends a contact whose `mustChangePassword` flag is set (S-11). */
  CHANGE_PASSWORD: '/customer-portal/change-password',
} as const;

export function customerEquipmentDetailPath(equipmentId: string): string {
  return `/customer-portal/equipment/${equipmentId}`;
}

export function customerJobDetailPath(jobId: string): string {
  return `/customer-portal/jobs/${jobId}`;
}

/** Stage D-i — the customer Request Thread for one modification round. */
export function customerRequestThreadPath(roundId: string): string {
  return `/customer-portal/requests/${roundId}`;
}

export type CustomerPortalRoute =
  (typeof CUSTOMER_PORTAL_ROUTES)[keyof typeof CUSTOMER_PORTAL_ROUTES];
